import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AppSettings, fetchAppSettingsFromDirectus, fetchAppSettingsFromDirectusByClientSlug } from '../../services/directus/directusClient';
import { getTextColourForBackground } from '../../utils/colourUtils';
import { applyThemeColours } from '../../utils/applyThemeColours';
import { applyBranding } from '../../utils/applyBranding';
import { THEME_DEFAULTS } from '../../constants/themeDefaults';
import { getClientSlugFromHostname } from '../../utils/getClientSlugFromHostname';

interface AppSettingsContextType {
  settings: AppSettings | null;
  loading: boolean;
  error: Error | null;
  getClientId: () => string;
  getEventOpenSessions: () => Array<{ date: string; start_time: string; end_time: string }>;
  isPintTokensDailyExpiryEnabled: () => boolean;
  getPintTokensPackPriceGbpPence: () => number;
  getPintTokensPackSize: () => number;
  getTextColour: () => string;
  getPrimaryColour: () => string;
  getSecondaryColour: () => string;
  getUiTheme: () => 'card' | 'list';
  getActionControlsMode: () => 'bar' | 'fab';
  getBadgeLayout: () => 'horizontal' | 'vertical';
  getCardBadgePosition: () => 'top_right' | 'number_column';
  getAvailabilityBadgeStyle: () => 'default' | 'glass';
  getLogoUrl: () => string;
  getBackgroundUrl: () => string;
  getAppName: () => string;
  isFeatureEnabled: (featureName: 'brewlette' | 'event_listings' | 'pint_tokens') => boolean;
}

const AppSettingsContext = createContext<AppSettingsContextType | undefined>(undefined);

interface AppSettingsProviderProps {
  children: ReactNode;
}

export const AppSettingsProvider: React.FC<AppSettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      const useDirectus = process.env.REACT_APP_USE_DIRECTUS !== 'false';
      
      if (!useDirectus) {
        applyThemeColours(null); // Apply defaults
        applyBranding(null);
        setLoading(false);
        return;
      }

      try {
        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
        const clientSlug = getClientSlugFromHostname(hostname);

        const appSettings = clientSlug
          ? await fetchAppSettingsFromDirectusByClientSlug(clientSlug)
          : await fetchAppSettingsFromDirectus();
        console.log('[AppSettingsProvider] Loaded settings:', appSettings);
        setSettings(appSettings);
        applyThemeColours(appSettings);
        applyBranding(appSettings);
      } catch (err) {
        console.error('[AppSettingsProvider] Error loading settings:', err);
        setError(err as Error);
        applyThemeColours(null); // Apply defaults
        applyBranding(null);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const getTextColour = (): string => {
    if (settings?.primary_text_colour) {
      return settings.primary_text_colour;
    }
    
    if (settings?.primary_colour) {
      return getTextColourForBackground(settings.primary_colour);
    }
    
    // Default: calculate based on default primary colour
    return getTextColourForBackground(THEME_DEFAULTS.PRIMARY_COLOUR);
  };

  const getPrimaryColour = (): string => {
    const colour = settings?.primary_colour || THEME_DEFAULTS.PRIMARY_COLOUR;
    return colour;
  };

  const getSecondaryColour = (): string => {
    return settings?.secondary_colour || THEME_DEFAULTS.SECONDARY_COLOUR;
  };

  const getUiTheme = (): 'card' | 'list' => {
    if (settings?.ui_theme === 'list') {
      return 'list';
    }

    return 'card';
  };

  const getActionControlsMode = (): 'bar' | 'fab' => {
    if (settings?.action_controls_mode === 'fab') {
      return 'fab';
    }

    return 'bar';
  };

  const getBadgeLayout = (): 'horizontal' | 'vertical' => {
    if (settings?.badge_layout === 'vertical') {
      return 'vertical';
    }

    return 'horizontal';
  };

  const getCardBadgePosition = (): 'top_right' | 'number_column' => {
    if (settings?.badge_position_card === 'number_column') {
      return 'number_column'
    }

    return 'top_right'
  };

  const getAvailabilityBadgeStyle = (): 'default' | 'glass' => {
    if (settings?.availability_badge_style === 'glass') {
      return 'glass';
    }

    return 'default';
  };

  const getLogoUrl = (): string => {
    return settings?.logo || '';
  };

  const getBackgroundUrl = (): string => {
    return settings?.background_image || '';
  };

  const getAppName = (): string => {
    return settings?.app_name || THEME_DEFAULTS.APP_NAME;
  };

  const getClientId = (): string => {
    return settings?.client_id || '';
  };

  const getEventOpenSessions = (): Array<{ date: string; start_time: string; end_time: string }> => {
    return settings?.event_open_sessions || [];
  };

  const isPintTokensDailyExpiryEnabled = (): boolean => {
    return settings?.pint_tokens_daily_expiry_enabled ?? false;
  };

  const getPintTokensPackPriceGbpPence = (): number => {
    const raw = Number(settings?.pint_tokens_pack_price_gbp_pence)
    return Number.isFinite(raw) && raw > 0 ? raw : 1500
  }

  const getPintTokensPackSize = (): number => {
    const raw = Number(settings?.pint_tokens_pack_size)
    return Number.isFinite(raw) && raw > 0 ? raw : 18
  }

  const isFeatureEnabled = (featureName: 'brewlette' | 'event_listings' | 'pint_tokens'): boolean => {
    if (featureName === 'brewlette') {
      return settings?.brewlette ?? false;
    }

    if (featureName === 'event_listings') {
      return settings?.event_listings ?? false;
    }

    if (featureName === 'pint_tokens') {
      if (typeof window !== 'undefined') {
        try {
          const forced = window.localStorage.getItem('force_pint_tokens')
          if (forced === 'true') {
            return true
          }
        } catch {
          // ignore
        }
      }

      return settings?.pint_tokens ?? false;
    }

    return true;
  };

  const value: AppSettingsContextType = {
    settings,
    loading,
    error,
    getClientId,
    getEventOpenSessions,
    isPintTokensDailyExpiryEnabled,
    getPintTokensPackPriceGbpPence,
    getPintTokensPackSize,
    getTextColour,
    getPrimaryColour,
    getSecondaryColour,
    getUiTheme,
    getActionControlsMode,
    getBadgeLayout,
    getCardBadgePosition,
    getAvailabilityBadgeStyle,
    getLogoUrl,
    getBackgroundUrl,
    getAppName,
    isFeatureEnabled,
  };

  return (
    <AppSettingsContext.Provider value={value}>
      {children}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = (): AppSettingsContextType => {
  const context = useContext(AppSettingsContext);
  if (context === undefined) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider');
  }
  return context;
};
